import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Skeleton
} from '@mui/material';
import {
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import { LeadershipAssignment } from '@/services/OrganizationSetupService';

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Level labels for display
const LEVEL_LABELS: Record<number, string> = {
  1: 'C-Suite',
  2: 'Vice Presidents',
  3: 'Directors',
  4: 'Managers'
};

interface NodeCardProps {
  node: LeadershipAssignment;
  reportsToName?: string;
  nodeRef?: (el: HTMLDivElement | null) => void;
}

const NodeCard: React.FC<NodeCardProps> = ({ node, reportsToName, nodeRef }) => {
  const theme = useTheme();

  return (
    <Card
      ref={nodeRef}
      elevation={0}
      data-node-id={node.id}
      sx={{
        position: 'relative',
        zIndex: 1,
        minWidth: 200,
        maxWidth: 280,
        borderRadius: 3,
        border: `2px solid ${node.color_code}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme.shadows[8],
          transform: 'scale(1.02)'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Avatar */}
          <Avatar
            src={node.employee_photo}
            sx={{
              width: 50,
              height: 50,
              backgroundColor: node.color_code,
              border: `2px solid ${node.color_code}`,
              fontWeight: 600
            }}
          >
            {getInitials(node.employee_name)}
          </Avatar>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Chip
              label={node.position_code}
              size="small"
              sx={{
                backgroundColor: node.color_code,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
                mb: 0.5
              }}
            />
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {node.employee_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {node.position_title}
            </Typography>
          </Box>
        </Box>

        {/* Reports To Info */}
        {reportsToName && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, textAlign: 'center', fontStyle: 'italic' }}
          >
            Reports to: {reportsToName}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

interface LevelRowProps {
  level: number;
  nodes: LeadershipAssignment[];
  allNodes: LeadershipAssignment[];
  nodeRefs: React.MutableRefObject<Map<number, HTMLDivElement | null>>;
}

const LevelRow: React.FC<LevelRowProps> = ({ level, nodes, allNodes, nodeRefs }) => {
  const theme = useTheme();

  // Find who each node reports to
  const getReportsToName = (node: LeadershipAssignment): string | undefined => {
    if (!node.reports_to_id) return undefined;
    const reportsTo = allNodes.find(n => n.id === node.reports_to_id);
    return reportsTo?.employee_name;
  };

  return (
    <Box sx={{ mb: 4 }}>
      {/* Level Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          gap: 1
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 600
          }}
        >
          {level}
        </Box>
        <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
          {LEVEL_LABELS[level] || `Level ${level}`}
        </Typography>
      </Box>

      {/* Level Nodes - Horizontal Row */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 3,
          flexWrap: 'wrap'
        }}
      >
        {nodes.map((node) => (
          <NodeCard
            key={node.id}
            node={node}
            reportsToName={getReportsToName(node)}
            nodeRef={(el) => nodeRefs.current.set(node.id, el)}
          />
        ))}
      </Box>
    </Box>
  );
};

// SVG Connector Lines Component
interface ConnectorLinesProps {
  allNodes: LeadershipAssignment[];
  nodeRefs: React.MutableRefObject<Map<number, HTMLDivElement | null>>;
  containerRef: React.RefObject<HTMLDivElement>;
}

const ConnectorLines: React.FC<ConnectorLinesProps> = ({ allNodes, nodeRefs, containerRef }) => {
  const theme = useTheme();
  const [lines, setLines] = useState<Array<{ x1: number; y1: number; x2: number; y2: number; isHorizontal?: boolean }>>([]);

  const calculateLines = useCallback(() => {
    if (!containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newLines: Array<{ x1: number; y1: number; x2: number; y2: number; isHorizontal?: boolean }> = [];

    // Get all Level 1 nodes for horizontal connection
    const level1Nodes = allNodes.filter(n => n.position_level === 1);

    if (level1Nodes.length > 1) {
      // Get positions of all level 1 nodes
      const level1Positions: Array<{ x: number; y: number }> = [];

      level1Nodes.forEach(node => {
        const el = nodeRefs.current.get(node.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          level1Positions.push({
            x: rect.left + rect.width / 2 - containerRect.left,
            y: rect.top + rect.height / 2 - containerRect.top  // Middle of card
          });
        }
      });

      // Sort by x position (left to right)
      level1Positions.sort((a, b) => a.x - b.x);

      // Draw horizontal line connecting all level 1 nodes
      if (level1Positions.length >= 2) {
        const firstNode = level1Positions[0];
        const lastNode = level1Positions[level1Positions.length - 1];
        newLines.push({
          x1: firstNode.x,
          y1: firstNode.y,
          x2: lastNode.x,
          y2: lastNode.y,
          isHorizontal: true
        });
      }
    }

    // Parent-child lines (for cross-level connections)
    allNodes.forEach(node => {
      if (!node.reports_to_id) return;

      const childEl = nodeRefs.current.get(node.id);
      const parentEl = nodeRefs.current.get(node.reports_to_id);

      if (childEl && parentEl) {
        const childRect = childEl.getBoundingClientRect();
        const parentRect = parentEl.getBoundingClientRect();

        // Calculate center positions relative to container
        const x1 = parentRect.left + parentRect.width / 2 - containerRect.left;
        const y1 = parentRect.bottom - containerRect.top;
        const x2 = childRect.left + childRect.width / 2 - containerRect.left;
        const y2 = childRect.top - containerRect.top;

        newLines.push({ x1, y1, x2, y2 });
      }
    });

    setLines(newLines);
  }, [allNodes, nodeRefs, containerRef]);

  useEffect(() => {
    calculateLines();

    // Recalculate on window resize
    const handleResize = () => calculateLines();
    window.addEventListener('resize', handleResize);

    // Use a small delay to ensure DOM is rendered
    const timeout = setTimeout(calculateLines, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, [calculateLines]);

  if (lines.length === 0) return null;

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0
      }}
    >
      {lines.map((line, index) => (
        line.isHorizontal ? (
          // Straight horizontal line for Level 1 (C-Suite)
          <line
            key={index}
            x1={line.x1}
            y1={line.y1}
            x2={line.x2}
            y2={line.y2}
            stroke={theme.palette.divider}
            strokeWidth={2}
          />
        ) : (
          // Bent line for parent-child relationships
          <path
            key={index}
            d={`M ${line.x1} ${line.y1}
                L ${line.x1} ${(line.y1 + line.y2) / 2}
                L ${line.x2} ${(line.y1 + line.y2) / 2}
                L ${line.x2} ${line.y2}`}
            stroke={theme.palette.divider}
            strokeWidth={2}
            fill="none"
          />
        )
      ))}
    </svg>
  );
};

const OrganizationTree: React.FC = () => {
  const theme = useTheme();
  const { leadershipHierarchy, hierarchyLoading } = useOrganizationSetupStore();
  const containerRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());

  // Group nodes by position_level
  const nodesByLevel = leadershipHierarchy.reduce((acc, node) => {
    const level = node.position_level || 1;
    if (!acc[level]) acc[level] = [];
    acc[level].push(node);
    return acc;
  }, {} as Record<number, LeadershipAssignment[]>);

  // Get sorted levels
  const sortedLevels = Object.keys(nodesByLevel)
    .map(Number)
    .sort((a, b) => a - b);

  // Loading State
  if (hierarchyLoading && leadershipHierarchy.length === 0) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
        <Skeleton variant="rounded" width={280} height={100} sx={{ borderRadius: 3 }} />
        <Box sx={{ width: 2, height: 24, backgroundColor: theme.palette.divider, my: 1 }} />
        <Box sx={{ display: 'flex', gap: 4 }}>
          <Skeleton variant="rounded" width={240} height={90} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" width={240} height={90} sx={{ borderRadius: 3 }} />
          <Skeleton variant="rounded" width={240} height={90} sx={{ borderRadius: 3 }} />
        </Box>
      </Box>
    );
  }

  // Empty State
  if (leadershipHierarchy.length === 0) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `2px dashed ${theme.palette.divider}`,
          p: 6,
          textAlign: 'center'
        }}
      >
        <Box
          sx={{
            width: 100,
            height: 100,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main + '15',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}
        >
          <AccountTreeIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
        </Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Organization Chart Empty
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
          Add leadership team members in the "Leadership Team" tab to see the organizational hierarchy here.
        </Typography>
      </Card>
    );
  }

  return (
    <Box
      sx={{
        py: 4,
        overflowX: 'auto',
        minHeight: 400
      }}
    >
      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 2,
          mb: 4,
          flexWrap: 'wrap'
        }}
      >
        <Chip
          size="small"
          sx={{ backgroundColor: '#FFB300', color: 'white' }}
          label="CEO"
        />
        <Chip
          size="small"
          sx={{ backgroundColor: '#1976d2', color: 'white' }}
          label="C-Suite"
        />
        <Chip
          size="small"
          sx={{ backgroundColor: '#43A047', color: 'white' }}
          label="Vice Presidents"
        />
        <Chip
          size="small"
          sx={{ backgroundColor: '#7B1FA2', color: 'white' }}
          label="Directors"
        />
      </Box>

      {/* Tree Container */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minWidth: 'fit-content',
          px: 4
        }}
      >
        {/* SVG Connector Lines */}
        <ConnectorLines
          allNodes={leadershipHierarchy}
          nodeRefs={nodeRefs}
          containerRef={containerRef}
        />

        {/* Render each level as a horizontal row */}
        {sortedLevels.map((level) => (
          <LevelRow
            key={level}
            level={level}
            nodes={nodesByLevel[level]}
            allNodes={leadershipHierarchy}
            nodeRefs={nodeRefs}
          />
        ))}
      </Box>

      {/* Instructions */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 4 }}
      >
        Leaders are organized by position level. Lines show reporting relationships.
      </Typography>
    </Box>
  );
};

export default OrganizationTree;
